// src/components/tickets/detail/SendConfirm.tsx
//
// The send-confirm step (Story 4.3 AC 8). "Nothing the console does ever emails
// a rep without this step" (EXPERIENCE.md). It shows the recipient, the EXACT
// text, and whether the draft was edited (display-only — the server value on the
// message row is the record), with the primary button labelled "Send to {rep}".
// Toast-only confirmation for outward sends is banned; this overlay is that gate.
//
// Overlay follows the repo idiom (fixed inset-0, role=dialog, Esc closes, focus
// returns to the trigger — handled by the Composer that opened it, with a stable
// fallback). One modal level.

import { useEffect, useRef } from 'react'
import type { DraftEditState } from '../../../utils/draftCompare'
import { draftEditStateLabel } from '../../../utils/draftCompare'

interface SendConfirmProps {
  // The rep display name, or null when the caller has no registered rep.
  recipientName: string | null
  body: string
  editState: DraftEditState
  sending: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function SendConfirm({
  recipientName,
  body,
  editState,
  sending,
  onConfirm,
  onCancel,
}: SendConfirmProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)
  const editLine = draftEditStateLabel(editState)

  // Focus the primary action on open (one modal level; the trigger restores
  // focus on close).
  useEffect(() => {
    confirmRef.current?.focus()
  }, [])

  const primaryLabel = recipientName
    ? `Send to ${recipientName}`
    : 'Record response'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4"
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-confirm-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
      >
        <h3
          id="send-confirm-title"
          className="mb-1 text-lg font-semibold text-gray-900"
        >
          {recipientName
            ? `Send to ${recipientName}?`
            : 'Record this response?'}
        </h3>
        <p className="mb-3 text-sm text-gray-600">
          {recipientName
            ? `This sends by email to ${recipientName} and marks the ticket answered.`
            : 'This caller has no registered rep — the response will be recorded but not emailed.'}
        </p>

        {/* The exact text that will be sent — no summarization, no edits. */}
        <div className="mb-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm leading-relaxed text-gray-900">
          {body}
        </div>

        {editLine ? (
          <p className="mb-3 text-xs text-gray-500">{editLine}</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={sending}
            className="btn-outline"
          >
            Back
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={sending}
            className="btn-primary"
          >
            {sending ? 'Sending…' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
